import { Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Hotkeys from 'react-hot-keys';
import { useTranslation } from 'react-i18next';
import ReactCrop, { Crop, PercentCrop } from 'react-image-crop';
import styled from 'styled-components';

import { QuestionMarkSvg } from '../../../../assets/icons';
import theme from '../../../../assets/theme';
import { useGameMediaFile } from '../../../../hooks/useGameMediaFile';
import { useGameMediaFileUploader } from '../../../../hooks/useGameMediaFileUploader';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import { RoundMedia } from '../../../../services/backendService/types';
import { readImageFiles } from '../../../../utils/fileDropBrowserUtils';
import { getFiles, SUPPORTED_IMAGE_MIMETYPES } from '../../../../utils/fileDropUtils';
import { generateUniqueId } from '../../../../utils/general';
import {
  CropConfig,
  cropToNaturalPx,
  naturalPxToCrop,
} from '../../../../utils/imageCropUtils';
import { Input } from '../../../_atoms/_Input';
import AutoCropSizer from '../../../_atoms/AutoCropSizer';
import { Button } from '../../../_atoms/Button';
import FileDropComponent from '../../../_atoms/FileDropComponent/FileDropComponent';
import MaxSizeMediaContent from '../../../_molecules/MaxSizeMediaContent';
import {
  MediaMimeType,
  OnElementsChangeProps,
  Slide,
  SlideElement,
  SlideElementImageType,
} from '../../../SlideComponents/slide.types';
import { createImageElement } from '../../../SlideComponents/slideUtil';

const UploadFilePicker = ({
  round,
  onRoundChange,
  isBackgroundMedia,
  droppedImageData,
  selectedElement,
  onElementsChange,
}: {
  round: Slide;
  onRoundChange: (round: Slide) => void;
  isBackgroundMedia: boolean;
  droppedImageData?: { src: string; mimeType: string };
  selectedElement: SlideElement | null;
  onElementsChange?: (prop: OnElementsChangeProps) => void;
}) => {
  const { t } = useTranslation('Host');
  const hiddenFileInputRef = useRef<HTMLInputElement>(null);
  const ref = useRef<HTMLImageElement>();
  const [imageData, setImageData] = useState<{ src: string; mimeType: string }>();
  const [isOnManualCrop, setIsOnManualCrop] = useState(false);
  const [cropConfig, setCropConfig] = useState<CropConfig>();
  const [crop, setCrop] = useState<Crop>();
  const [file, setFile] = useState<File>();
  const [droppedFile, setDroppedFile] = useState<File | null>();
  const [altText, setAltText] = useState<string>();

  const [fileToUpload, setFileToUpload] = useState<{
    mimeType: string;
    fileId: string;
    altText?: string;
    width?: number;
    height?: number;
    cropConfig?: CropConfig;
  }>();

  const selectedMedia: RoundMedia | undefined = useMemo(() => {
    if (isBackgroundMedia) return round?.backgroundMedia;
    if (!selectedElement) return;
    if (
      selectedElement.type === 'image' &&
      selectedElement.mediaFileId &&
      selectedElement.mediaMimetype
    ) {
      return {
        fileId: selectedElement.mediaFileId,
        fileMimeType: selectedElement.mediaMimetype,
      };
    }
    return;
  }, [selectedElement, isBackgroundMedia, round?.backgroundMedia]);

  const { data } = useGameMediaFile(selectedElement?.mediaFileId, true);
  const { tenantId } = useRouterMatch();
  const { upload, isSuccess, isUploading } = useGameMediaFileUploader(tenantId);

  useEffect(() => {
    // Overwrite cropconfig and altText with existing element data.
    if (
      !selectedElement ||
      !selectedElement.mediaFileId ||
      !selectedElement.mediaMimetype
    )
      return;
    setCropConfig(selectedElement?.mediaCropConfig || undefined);
    setAltText(selectedElement?.mediaAltText || undefined);
  }, [selectedElement]);

  const handleCtrlVPress = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      const pastedUrl = e.clipboardData.getData('text');
      const items = e.clipboardData.items;
      if (pastedUrl !== '') {
        return; // do nothing if not an image file
      } else {
        for (let i = 0; i < items.length; i++) {
          // Skip content if not image
          if (items[i].type.indexOf('image') === -1) continue;
          // Retrieve image from clipboard as blob
          const blob = items[i].getAsFile();
          setDroppedFile(blob);
        }
      }
    },

    [],
  );

  useEffect(() => {
    window.addEventListener('paste', handleCtrlVPress);

    return () => window.removeEventListener('paste', handleCtrlVPress);
  }, [handleCtrlVPress]);

  useEffect(() => {
    if (droppedImageData) {
      // Parent component drops an image file and wants to do something in this component.
      setImageData(droppedImageData);
    }
  }, [droppedImageData]);

  useEffect(() => {
    if (data && selectedElement?.mediaMimetype) {
      setImageData({ src: data, mimeType: selectedElement?.mediaMimetype });
    }
  }, [data, selectedElement]);

  useEffect(() => {
    if (file) {
      readImageFiles(file)
        .then(setImageData)
        .catch(e => {
          // eslint-disable-next-line no-console
          console.error(e);
        });
    }
  }, [file]);

  useEffect(() => {
    if (droppedFile) {
      setFile(droppedFile);
    }
  }, [droppedFile]);

  useEffect(() => {
    if (isSuccess && fileToUpload) {
      const rootMedia: RoundMedia = {
        fileId: fileToUpload.fileId,
        fileMimeType: fileToUpload.mimeType,
        cropConfig: fileToUpload.cropConfig || null,
      };
      if (isBackgroundMedia) {
        rootMedia.brightness = 0.3;
        onRoundChange({
          ...round,
          backgroundMedia: rootMedia,
        });
      } else {
        if (selectedElement) return;
        const imageElement = createImageElement({
          cropConfig: cropConfig,
          mediaFileId: rootMedia.fileId,
          mediaAltText: altText,
          height: fileToUpload.height,
          width: fileToUpload.width,
          mediaMimeType: fileToUpload.mimeType as MediaMimeType,
          objectFit: 'fill',
        });

        onRoundChange({
          ...round,
          elements: [...round.elements, imageElement],
        });
      }
      setFileToUpload(undefined);
    }
  }, [
    isSuccess,
    fileToUpload,
    round,
    onRoundChange,
    isBackgroundMedia,
    selectedElement,
    cropConfig,
    altText,
    onElementsChange,
  ]);

  const [naturalImageProps, setNaturalImageProps] = useState<{
    height: number;
    width: number;
  }>();

  const handleSetCropToPerfect = () => {
    setIsOnManualCrop(true);
    setCrop({
      unit: '%',
      width: 70,
      aspect: 16 / 8,
      y: 1,
      x: 1,
    });
  };

  const handleSetCropToFreeForm = () => {
    setIsOnManualCrop(true);
    setCrop({
      unit: '%',
      width: 50,
      height: 50,
      y: 25,
      x: 25,
    });
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files[0]) {
      const file = files.item(0);
      if (file) {
        setFile(file);
      }
    }
  };

  const handleActivate = () => {
    if (hiddenFileInputRef.current) {
      hiddenFileInputRef.current.value = '';
      hiddenFileInputRef.current!.click();
    }
  };

  const handleFilesDropped = (files: FileList | null) => {
    if (files) {
      const parsedFiles = getFiles(files);
      const invalidFiles = parsedFiles.filter(
        file => !SUPPORTED_IMAGE_MIMETYPES.includes(file.type),
      );
      if (invalidFiles.length > 0) {
        alert('Invalid file type, please try again');
        return;
      }

      if (parsedFiles.length > 0) {
        setDroppedFile(parsedFiles[0]);
      }
    }
  };

  const handleCropImageSizeChanged = (height: number, width: number) => {
    if (naturalImageProps && !isOnManualCrop && selectedElement?.mediaCropConfig) {
      const newCrop = naturalPxToCrop(
        { height: naturalImageProps?.height, width: naturalImageProps.width },
        { height: height, width: width },
        selectedElement.mediaCropConfig,
      );
      setCrop(newCrop);
      return;
    }
    if (!isOnManualCrop) {
      setCrop({
        unit: 'px',
        width: width,
        height: height,
        y: 0,
        x: 0,
      });
    }
  };

  const handleImageLoaded = async (element: HTMLImageElement) => {
    ref.current = element;
    setNaturalImageProps({ height: element.naturalHeight, width: element.naturalWidth });
  };

  const getCroppedImg = async (
    image: HTMLImageElement,
    crop: Crop,
    _fileName: string,
  ) => {
    if (crop.x !== undefined && crop.y !== undefined && crop.width && crop.height) {
      return {
        mediaProps: cropToNaturalPx(
          crop,
          { height: image.naturalHeight, width: image.naturalWidth },
          { height: image.height, width: image.width },
        ),
      };
    }
  };

  const handleComplete = async (crop: Crop) => {
    if (ref.current && crop.width && crop.height && isOnManualCrop) {
      const res = await getCroppedImg(ref.current, crop, 'newFile.jpeg');
      if (res) {
        const { mediaProps } = res;
        setCropConfig(mediaProps);
      }
    }
  };

  const handleCropChange = (crop: Crop, _percentCrop: PercentCrop) => {
    setCrop(crop);
  };
  const handleFileUpload = ({
    imgData,
    mimeType,
    altText,
    cropConfig,
    width,
    height,
  }: {
    imgData: string;
    mimeType: string;
    altText?: string;
    cropConfig?: CropConfig;
    width?: number;
    height?: number;
  }) => {
    if (imgData.startsWith('blob') && selectedMedia) {
      //Skip uploading, as the file is already existing and not changed
      const newMedia: RoundMedia = {
        ...selectedMedia,
        fileAltText: altText || undefined,
        cropConfig: cropConfig || undefined,
      };
      if (isBackgroundMedia) {
        // Background media does not care about crop or elements.
        newMedia.brightness = 0.3;
        onRoundChange({
          ...round,
          backgroundMedia: newMedia,
        });
        setFileToUpload(undefined);
        return;
      } else {
        if (!selectedElement) {
          // Unexpected state. Should not happen
          return;
        }
        const element = selectedElement as SlideElementImageType;
        // Normal media element
        const editedElement: SlideElementImageType = {
          ...element,
          mediaAltText: altText || undefined,
          mediaCropConfig: cropConfig || undefined,
          imageObjectFit: 'fill',
          height: cropConfig?.height || height || element.height,
          width: cropConfig?.width || width || element.width,
        };
        if (!onElementsChange) return;
        onElementsChange({ updatedElements: [editedElement] }); // make sure to also update the selected element in case of new draggin resizing etc.
        onRoundChange({
          ...round,
          elements: round.elements.map(e =>
            e.id === editedElement.id ? editedElement : e,
          ),
        });
      }

      return;
    }
    const fileId = generateUniqueId().toString();
    setFileToUpload({ mimeType, fileId, altText, cropConfig, width, height });
    upload({ imgData, fileId });
  };

  const handleSubmit = () => {
    if (imageData) {
      handleFileUpload({
        imgData: imageData.src,
        mimeType: imageData.mimeType,
        width: cropConfig?.width || naturalImageProps?.width,
        height: cropConfig?.height || naturalImageProps?.height,
        altText,
        cropConfig,
      });
      return;
    }
  };

  const handleChange = () => {
    setImageData(undefined);
  };

  return (
    <Container>
      <Hotkeys keyName={'Enter'} onKeyUp={handleSubmit} />
      {imageData &&
        (imageData.mimeType === 'image/png' || imageData.mimeType === 'image/jpeg') && (
          <RowContainer>
            <Button
              type="secondary"
              disabled={!!crop?.aspect}
              onClick={() => handleSetCropToPerfect()}
            >
              {t('Perfect')}
            </Button>
            <Button
              type="secondary"
              disabled={!crop?.aspect}
              onClick={() => handleSetCropToFreeForm()}
            >
              {t('Free form')}
            </Button>
          </RowContainer>
        )}
      {!imageData && (
        <FileDropComponentContainer>
          <input
            ref={hiddenFileInputRef}
            type="file"
            name="myFile"
            multiple={false}
            accept={SUPPORTED_IMAGE_MIMETYPES.join(',')}
            onChange={onChange}
            style={{ display: 'none' }}
          />
          <FileDropComponent
            onClick={handleActivate}
            onFilesDropped={handleFilesDropped}
          />
        </FileDropComponentContainer>
      )}
      {imageData && (
        <>
          <FileDropComponentContainer>
            <ImageCropContainer>
              {imageData &&
                SUPPORTED_IMAGE_MIMETYPES &&
                imageData.mimeType !== 'image/gif' && (
                  <div style={{ width: '100%', height: '30vh' }}>
                    <MaxSizeMediaContent>
                      <AutoCropSizer
                        mediaNaturalHeight={naturalImageProps?.height}
                        mediaNaturalWidth={naturalImageProps?.width}
                        onSizeChange={handleCropImageSizeChanged}
                      >
                        <ReactCrop
                          src={imageData.src}
                          crop={crop}
                          ruleOfThirds
                          onDragStart={() => {
                            setIsOnManualCrop(true);
                          }}
                          onImageLoaded={handleImageLoaded}
                          onComplete={handleComplete}
                          onChange={handleCropChange}
                          imageStyle={{
                            visibility: !!naturalImageProps ? 'visible' : 'hidden',
                          }}
                        />
                      </AutoCropSizer>
                    </MaxSizeMediaContent>
                  </div>
                )}
              {imageData && imageData.mimeType === 'image/gif' && (
                <div style={{ width: '100%', height: '30vh' }}>
                  <MaxSizeMediaContent>
                    <img
                      src={imageData.src}
                      alt={altText}
                      style={{ height: '100%', width: '100%', objectFit: 'contain' }}
                    />
                  </MaxSizeMediaContent>
                </div>
              )}
            </ImageCropContainer>
          </FileDropComponentContainer>
          <BottomActionbarContainer>
            <ActionBarResponsiveAltTextContainer>
              <p
                style={{ fontWeight: 'bold', color: theme.colors.black, marginBottom: 5 }}
              >
                {t('Alt text')}
                <Tooltip
                  title={t(
                    `Visually impaired participants use a screen reader to understand the image in context.  By adding the alt text you include everyone in your audience`,
                  )}
                >
                  <TooltipIconWrapper>
                    <QuestionMarkSvg />
                  </TooltipIconWrapper>
                </Tooltip>
              </p>
              <Input
                value={altText}
                onChange={e => setAltText(e.target.value)}
                placeholder={t('Describe the image for the audience unable to see it')}
                style={{ width: '100%' }}
              />
            </ActionBarResponsiveAltTextContainer>
          </BottomActionbarContainer>
          <BottomActionbarContainer>
            <ActionBarResponsiveButtonContainer style={{ marginRight: 10 }}>
              <Button
                styles={{
                  flex: 0,
                  color: theme.colors.black,
                  borderColor: theme.colors.black,
                }}
                disabled={isUploading}
                height="42px"
                width="88px"
                type="tertiary"
                onClick={() => {
                  handleChange();
                }}
              >
                {t('Change')}
              </Button>
            </ActionBarResponsiveButtonContainer>

            <ActionBarResponsiveButtonContainer style={{ marginLeft: 10 }}>
              <Button
                styles={{ flex: 0 }}
                height="42px"
                width="88px"
                type="primary"
                disabled={isUploading}
                loading={isUploading}
                onClick={() => {
                  handleSubmit();
                }}
              >
                {t('Apply')}
              </Button>
            </ActionBarResponsiveButtonContainer>
          </BottomActionbarContainer>
        </>
      )}
    </Container>
  );
};

export default UploadFilePicker;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TooltipIconWrapper = styled.span`
  margin-left: 10px;
  &:hover {
    cursor: pointer;
  }
  svg {
    height: 16px;
    width: 16px;
    color: ${theme.colors.black};
  }
`;

const ActionBarResponsiveAltTextContainer = styled.div`
  flex: 1;
  @media (max-width: 500px) {
    order: 0;
    flex: 1 1 100%;
  }
`;

const ActionBarResponsiveButtonContainer = styled.div`
  flex: 0;
  @media (max-width: 500px) {
    order: 1;
    margin-top: 10px;
  }
`;

const BottomActionbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  /* padding-left: 30px;
  padding-right: 30px; */
  flex: 0;

  @media (max-width: 600px) {
    padding-left: 0px;
    padding-right: 0px;
  }
  @media (max-width: 500px) {
    flex-wrap: wrap;
  }
`;

const RowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  & > *:not(:first-child) {
    margin-left: 10px;
  }
  & > *:not(:last-child) {
    margin-right: 10px;
  }
`;

const ImageCropContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  & img {
    max-height: 100%;
    max-width: 100%;
    border-radius: 5px;
  }

  /* Make sure react crop positions the image according to the max size image positioner */

  .ReactCrop {
    height: 100%;
    width: 100%;
  }

  .ReactCrop__crop-selection {
    border: 3px solid ${theme.colors.blue};
  }

  .ReactCrop__drag-handle::after {
    border-radius: 5000px;
    background-color: ${theme.colors.blue};
    border: 1px solid ${theme.colors.blue};
    box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.1);
  }
`;

const FileDropComponentContainer = styled.div`
  width: 100%;
  flex: 1 1 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;
